// PUBLIC — no authentication required.
// To disable: comment out the /public/design-docs/:slug routes in App.js.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import DocViewerTourJoyride from '../shared/DocViewerTourJoyride';
import DesignDocLegendBar from '../shared/DesignDocLegendBar';

const TABS = [
  { key: 'context',   label: 'C4 Context',      endpoint: 'context'   },
  { key: 'container', label: 'C4 Container',     endpoint: 'container' },
  { key: 'component', label: 'C4 Component',     endpoint: 'component' },
  { key: 'uc',        label: 'Use Case Diagram', endpoint: 'uc'        },
];

const WEBSITE_LABELS = {
  catshop:   'Cat Supplies Shop',
  healthpro: 'Health Pro Management',
  sample:    'Sample Application',
};

// ── Zoomable SVG canvas ───────────────────────────────────────────────────────
function ZoomableSvgViewer({ svg }) {
  const viewportRef  = useRef(null);
  const liveRef      = useRef({ zoom: 1, panX: 0, panY: 0 });
  const isDragging   = useRef(false);
  const dragStart    = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const [zoom, setZoom]       = useState(1);
  const [pan,  setPan]        = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);

  const applyTransform = useCallback((newZoom, newPan) => {
    const clamped = Math.min(4, Math.max(0.05, newZoom));
    liveRef.current = { zoom: clamped, panX: newPan.x, panY: newPan.y };
    setZoom(clamped);
    setPan(newPan);
  }, []);

  const fitToCanvas = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const svgEl = vp.querySelector('svg');
    if (!svgEl) return;
    let svgW, svgH;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const p = vb.trim().split(/[\s,]+/);
      svgW = parseFloat(p[2]);
      svgH = parseFloat(p[3]);
    } else {
      svgW = parseFloat(svgEl.getAttribute('width'))  || 800;
      svgH = parseFloat(svgEl.getAttribute('height')) || 600;
    }
    const viewW = vp.clientWidth;
    const viewH = vp.clientHeight;
    const fit   = Math.min((viewW - 40) / svgW, (viewH - 40) / svgH);
    applyTransform(fit, {
      x: (viewW - svgW * fit) / 2,
      y: (viewH - svgH * fit) / 2,
    });
  }, [applyTransform]);

  useEffect(() => {
    if (!svg) return;
    requestAnimationFrame(() => fitToCanvas());
  }, [svg, fitToCanvas]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = e => {
      e.preventDefault();
      const { zoom: curZoom, panX, panY } = liveRef.current;
      const factor  = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newZoom = Math.min(4, Math.max(0.05, curZoom * factor));
      const rect    = vp.getBoundingClientRect();
      const mx      = e.clientX - rect.left;
      const my      = e.clientY - rect.top;
      applyTransform(newZoom, {
        x: mx - (mx - panX) * (newZoom / curZoom),
        y: my - (my - panY) * (newZoom / curZoom),
      });
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [applyTransform]);

  const handleSlider = val => {
    const vp = viewportRef.current;
    const newZoom = val / 100;
    if (!vp) { applyTransform(newZoom, { x: liveRef.current.panX, y: liveRef.current.panY }); return; }
    const { zoom: curZoom, panX, panY } = liveRef.current;
    const cx = vp.clientWidth  / 2;
    const cy = vp.clientHeight / 2;
    applyTransform(newZoom, {
      x: cx - (cx - panX) * (newZoom / curZoom),
      y: cy - (cy - panY) * (newZoom / curZoom),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div id="pub-doc-zoom-controls" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderBottom: '1px solid #dee2e6',
        background: '#f8f9fa', flexShrink: 0,
      }}>
        <button onClick={fitToCanvas} style={btnStyle}>Fit</button>
        <button onClick={() => {
          const { zoom: z, panX, panY } = liveRef.current;
          const vp = viewportRef.current;
          const cx = vp ? vp.clientWidth / 2 : 0;
          const cy = vp ? vp.clientHeight / 2 : 0;
          const nz = Math.min(4, z * 1.2);
          applyTransform(nz, { x: cx - (cx - panX) * (nz / z), y: cy - (cy - panY) * (nz / z) });
        }} style={btnStyle}>+</button>
        <button onClick={() => {
          const { zoom: z, panX, panY } = liveRef.current;
          const vp = viewportRef.current;
          const cx = vp ? vp.clientWidth / 2 : 0;
          const cy = vp ? vp.clientHeight / 2 : 0;
          const nz = Math.max(0.05, z / 1.2);
          applyTransform(nz, { x: cx - (cx - panX) * (nz / z), y: cy - (cy - panY) * (nz / z) });
        }} style={btnStyle}>−</button>
        <input
          type="range" min={5} max={400} step={1}
          value={Math.round(zoom * 100)}
          onChange={e => handleSlider(parseInt(e.target.value, 10))}
          style={{ width: 120, cursor: 'pointer', accentColor: '#0d6efd' }}
        />
        <span style={{ minWidth: 40, fontSize: 13, color: '#6c757d' }}>{Math.round(zoom * 100)}%</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#adb5bd' }}>
          Scroll to zoom · Middle/right-drag to pan
        </span>
      </div>

      <div
        id="pub-doc-viewport"
        ref={viewportRef}
        style={{
          flex: 1, overflow: 'hidden', background: '#f8f9fa',
          position: 'relative', cursor: panning ? 'grabbing' : 'default',
          userSelect: 'none',
        }}
        onContextMenu={e => { if (isDragging.current) e.preventDefault(); }}
        onMouseDown={e => {
          if (e.button !== 1 && e.button !== 2) return;
          e.preventDefault();
          isDragging.current = true;
          dragStart.current = { x: e.clientX, y: e.clientY, panX: liveRef.current.panX, panY: liveRef.current.panY };
          setPanning(true);
        }}
        onMouseMove={e => {
          if (!isDragging.current) return;
          applyTransform(liveRef.current.zoom, {
            x: dragStart.current.panX + (e.clientX - dragStart.current.x),
            y: dragStart.current.panY + (e.clientY - dragStart.current.y),
          });
        }}
        onMouseUp={e => { if (e.button === 1 || e.button === 2) { isDragging.current = false; setPanning(false); } }}
        onMouseLeave={() => { isDragging.current = false; setPanning(false); }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: svg }}
          style={{
            position: 'absolute', transformOrigin: '0 0',
            transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}

const btnStyle = {
  padding: '3px 10px', fontSize: 13, cursor: 'pointer',
  border: '1px solid #dee2e6', borderRadius: 4,
  background: '#fff', color: '#212529', lineHeight: 1.4,
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PublicDesignDocViewer() {
  const { slug } = useParams();
  const [activeTab,   setActiveTab]   = useState(TABS[0].key);
  const [svgCache,    setSvgCache]    = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [joyrideRun,  setJoyrideRun]  = useState(false);
  const [tourKey,     setTourKey]     = useState(0);
  const loadedRef = useRef(new Set());

  const loadTab = useCallback(async (tabKey) => {
    if (loadedRef.current.has(tabKey)) return;
    loadedRef.current.add(tabKey);
    setSvgCache(prev => ({ ...prev, [tabKey]: 'loading' }));
    const tab = TABS.find(t => t.key === tabKey);
    try {
      const res = await fetch(`/api/public/design-docs/${slug}/${tab.endpoint}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSvgCache(prev => ({ ...prev, [tabKey]: data.svg || 'error' }));
    } catch {
      setSvgCache(prev => ({ ...prev, [tabKey]: 'error' }));
    }
  }, [slug]);

  useEffect(() => {
    if (!WEBSITE_LABELS[slug]) { setGlobalError(`Unknown website slug: "${slug}"`); return; }
    loadTab(TABS[0].key);
    if (!localStorage.getItem('pub_doc_tour_seen')) {
      localStorage.setItem('pub_doc_tour_seen', '1');
      requestAnimationFrame(() => setJoyrideRun(true));
    }
  }, [slug, loadTab]);

  const handleTabChange = newKey => {
    if (newKey === activeTab) return;
    setActiveTab(newKey);
    loadTab(newKey);
  };

  if (globalError) {
    return <div style={{ padding: 24 }}><Alert variant="danger">{globalError}</Alert></div>;
  }

  const tabSvg = svgCache[activeTab];

  return (
    <div id="pub-doc-viewer-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Inter, sans-serif' }}>
      <DocViewerTourJoyride
        key={tourKey}
        run={joyrideRun}
        onFinish={() => setJoyrideRun(false)}
        idPrefix="pub-"
        includeDocIntro={false}
      />
      {/* Tab bar */}
      <div id="pub-doc-tab-bar" style={{
        display: 'flex', alignItems: 'stretch',
        borderBottom: '2px solid #dee2e6',
        background: '#fff', flexShrink: 0,
        paddingLeft: 12,
      }}>
        <span style={{
          padding: '10px 14px', fontSize: 13, fontWeight: 700,
          color: '#212529', alignSelf: 'center', whiteSpace: 'nowrap',
        }}>
          {WEBSITE_LABELS[slug]} — Design Documents
        </span>
        <button
          onClick={() => { setTourKey(k => k + 1); setJoyrideRun(true); }}
          title="How to use this viewer"
          style={{
            ...btnStyle,
            flexShrink: 0, margin: '0 12px', alignSelf: 'center',
            fontSize: 12, padding: '4px 12px',
          }}
        >
          ? How to use
        </button>
        <div style={{ width: 1, background: '#dee2e6', margin: '6px 0' }} />
        <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', flex: 1, paddingLeft: 12 }}>
          {TABS.map(tab => {
            const active = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                style={{
                  padding: '10px 18px', fontSize: 13,
                  fontWeight: active ? 700 : 400,
                  color: active ? '#0d6efd' : '#6c757d',
                  background: 'none', border: 'none',
                  borderBottom: active ? '2px solid #0d6efd' : '2px solid transparent',
                  marginBottom: -2, cursor: 'pointer',
                  whiteSpace: 'nowrap', transition: 'color 0.15s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend bar (the C4 diagrams carry no key of their own) */}
      <DesignDocLegendBar tab={activeTab} idPrefix="pub-" />

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {!tabSvg || tabSvg === 'loading' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 24 }}>
            <Spinner animation="border" size="sm" />
            <span style={{ color: '#6c757d' }}>Loading diagram…</span>
          </div>
        ) : tabSvg === 'error' ? (
          <div style={{ padding: 24 }}>
            <Alert variant="warning">Diagram not available for this website.</Alert>
          </div>
        ) : (
          <ZoomableSvgViewer key={activeTab} svg={tabSvg} />
        )}
      </div>
    </div>
  );
}
